/**
 * Шаг «Должности» визарда профрисков (centrika-docs!26,
 * `component.create-profrisk-order.positions`). Сверху вниз: информационный блок, кнопки
 * «Из структуры компании» (основное действие) и «Добавить вручную» (дополнительное) в одну
 * строку со счётчиком «Добавлено: N должностей», список развёрнутых форм должностей либо пустое
 * состояние, пояснение под списком. Экрана-списка с карточками и отдельного экрана формы больше
 * нет — каждая должность показана развёрнутой формой со сквозным номером (`PositionForm`).
 *
 * Добавление: «Добавить вручную» — сразу пустая форма (`source: 'manual'`, без диалога),
 * «Из структуры компании» — модальное окно множественного выбора (`PositionsStructureModal`),
 * которое добавляет отмеченные должности одним атомарным батчем. Штатные единицы из структуры
 * не подставляются, поэтому новые формы сразу подсвечены как незаполненные. Удаление — кнопка
 * «Убрать» без подтверждения, оставшиеся формы перенумеровываются.
 *
 * Подразделения структуры грузятся ОДИН раз на шаг и передаются во все формы: созданное в любом
 * выпадающем списке подразделение появляется во всех формах шага без перезагрузки страницы.
 */
import { useEffect, useRef, useState } from 'react'
import { FileSpreadsheet, Plus, Users } from 'lucide-react'
import { ApiError, companyApi, profriskApi, type Department, type ProfriskPosition } from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import { positionsCountLabel } from '@/lib/profrisk'
import { PositionForm } from './position-form'
import { PositionsStructureModal } from './positions-structure-modal'
import { ORDER_IMPORT_BUTTON } from '@/orders/order-import-dialog'
import {
  PROFRISK_POSITIONS_ADD_MANUAL,
  PROFRISK_POSITIONS_EMPTY,
  PROFRISK_POSITIONS_FROM_STRUCTURE,
  PROFRISK_POSITIONS_INFO_BODY,
  PROFRISK_POSITIONS_INFO_TITLE,
  PROFRISK_POSITIONS_LIST_NOTE,
} from './profrisk-copy'
import { DEPARTMENT_SELECT_DUPLICATE } from '@/company/department-select'
import { Panel } from '@/design-system/components/layout/panel'
import { Button } from '@/design-system/components/primitives/button'
import { Alert } from '@/design-system/components/feedback/alert'
import { EmptyState } from '@/design-system/components/feedback/empty-state'

/** Текст ошибки создания подразделения: дубль (409) — текст интерфейса, остальное — от сервера. */
function departmentCreateError(err: unknown): string {
  if (err instanceof ApiError && err.status === 409) return DEPARTMENT_SELECT_DUPLICATE
  return getErrorMessage(err, 'Не удалось создать подразделение')
}

export interface PositionsStepProps {
  orderId: string
  positions: ProfriskPosition[]
  readOnly: boolean
  /** Заменить весь список (после добавления/удаления — уже сохранено на сервере). */
  onPositionsChange: (positions: ProfriskPosition[]) => void
  /** Правка одной должности (автосохранение updatePosition — у родителя). */
  onPositionEdit: (position: ProfriskPosition) => void
  /**
   * Долить отложенный (debounced) PATCH ДО правки другой должности и до удаления. Анти-гонка:
   * автосейв визарда один на все должности, поэтому без flush правка должности A терялась бы,
   * если правка B перепланирует автосейв в окне дебаунса (все формы шага видны одновременно).
   */
  onFlushEdits: () => Promise<void>
  /**
   * Открыть загрузку «Анкеты и формы (РИСКИ)» (order-import-legacy-forms). Диалог живёт в мастере:
   * загрузка меняет и анкету (контакт, пункты «Да/Нет», инструкции), которую держит мастер.
   */
  onImportFile?: () => void
}

export function PositionsStep({
  orderId,
  positions,
  readOnly,
  onPositionsChange,
  onPositionEdit,
  onFlushEdits,
  onImportFile,
}: PositionsStepProps) {
  const [departments, setDepartments] = useState<Department[]>([])
  const [structureOpen, setStructureOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Должность, чья правка сейчас лежит в окне дебаунса автосейва (см. onFlushEdits).
  const editingRef = useRef<string | null>(null)

  useEffect(() => {
    if (readOnly) return
    const controller = new AbortController()
    companyApi
      .listDepartments(controller.signal)
      .then((res) => {
        if (!controller.signal.aborted) setDepartments(res.items)
      })
      .catch(() => undefined)
    return () => controller.abort()
  }, [readOnly])

  /** Правка формы: перед сменой редактируемой должности доливаем отложенный PATCH предыдущей. */
  function edit(next: ProfriskPosition) {
    if (editingRef.current && editingRef.current !== next.id) void onFlushEdits()
    editingRef.current = next.id
    onPositionEdit(next)
  }

  async function addManually() {
    setError(null)
    setBusy(true)
    try {
      // Ручное добавление без диалога: пустая форма с меткой источника «вручную».
      const created = await profriskApi.addPosition(orderId, { source: 'manual' })
      onPositionsChange([...positions, created])
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось добавить должность'))
    } finally {
      setBusy(false)
    }
  }

  async function remove(position: ProfriskPosition) {
    setError(null)
    setBusy(true)
    try {
      // Отложенный PATCH удаляемой должности должен уйти ДО DELETE (иначе прилетит 404).
      await onFlushEdits()
      if (editingRef.current === position.id) editingRef.current = null
      await profriskApi.deletePosition(orderId, position.id)
      onPositionsChange(positions.filter((p) => p.id !== position.id))
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось удалить должность'))
    } finally {
      setBusy(false)
    }
  }

  async function createDepartment(name: string): Promise<Department> {
    const created = await companyApi.createDepartment(name)
    // Общий список шага — созданное подразделение появляется во ВСЕХ формах без перезагрузки.
    setDepartments((prev) => (prev.some((d) => d.id === created.id) ? prev : [...prev, created]))
    return created
  }

  const counter = positions.length > 0 ? `Добавлено: ${positionsCountLabel(positions.length)}` : null

  return (
    <div className="flex flex-col gap-4">
      {/* В режиме просмотра отправленного заказа информационный блок не отображается. */}
      {!readOnly ? (
        <Alert variant="info" title={PROFRISK_POSITIONS_INFO_TITLE}>
          {PROFRISK_POSITIONS_INFO_BODY}
        </Alert>
      ) : null}

      {!readOnly ? (
        <div className="flex flex-wrap items-center gap-3">
          <Button
            onClick={() => setStructureOpen(true)}
            leftIcon={<Users className="size-4 stroke-current" aria-hidden />}
          >
            {PROFRISK_POSITIONS_FROM_STRUCTURE}
          </Button>
          <Button
            variant="secondary"
            disabled={busy}
            onClick={() => void addManually()}
            leftIcon={<Plus className="size-4 stroke-current" aria-hidden />}
          >
            {PROFRISK_POSITIONS_ADD_MANUAL}
          </Button>
          {onImportFile ? (
            <Button
              variant="secondary"
              disabled={busy}
              onClick={onImportFile}
              leftIcon={<FileSpreadsheet className="size-4 stroke-current" aria-hidden />}
            >
              {ORDER_IMPORT_BUTTON}
            </Button>
          ) : null}
          {counter ? <span className="text-txt-secondary">{counter}</span> : null}
        </div>
      ) : counter ? (
        <p className="text-txt-secondary">{counter}</p>
      ) : null}

      {error ? <Alert variant="danger">{error}</Alert> : null}

      {positions.length === 0 ? (
        <Panel>
          <EmptyState icon={Plus} title={PROFRISK_POSITIONS_EMPTY} />
        </Panel>
      ) : (
        <div className="flex flex-col gap-4">
          {positions.map((position, i) => (
            <PositionForm
              key={position.id}
              position={position}
              index={i + 1}
              readOnly={readOnly}
              departments={departments}
              busy={busy}
              onChange={edit}
              onRemove={() => void remove(position)}
              onCreateDepartment={createDepartment}
              departmentCreateError={departmentCreateError}
            />
          ))}
        </div>
      )}

      {/* Пояснение под списком либо пустым состоянием; в режиме просмотра не отображается. */}
      {!readOnly ? (
        <p className="text-13 text-txt-tertiary">{PROFRISK_POSITIONS_LIST_NOTE}</p>
      ) : null}

      {structureOpen && !readOnly ? (
        <PositionsStructureModal
          orderId={orderId}
          positions={positions}
          onClose={() => setStructureOpen(false)}
          onAdded={(added) => {
            if (added.length > 0) onPositionsChange([...positions, ...added])
          }}
        />
      ) : null}
    </div>
  )
}
